/**
 * KsefAutomationRulesSection — Automatyzacja KSeF: reguły warunki→akcje per firma (Faza 5).
 *
 * Warunki (AND): kierunek, NIP kontrahenta (biała/czarna lista), nazwa kontrahenta (zawiera),
 * numer faktury (zawiera), waluty, zakres brutto/netto, typy faktur. Akcje (lista, po kolei):
 * utworzenie rekordu w module / wbudowanej faktury zakupu-sprzedaży (+ start obiegu) / ustawienie
 * statusu. Test „na sucho" pokazuje pasujące faktury. Backend: /api/ksef/automation-rules (+ [id], /test).
 *
 * Teksty przez i18next (namespace `tenant.ksefAutomation`). Mapy stałych trzymają KLUCZE i18n
 * (są poza komponentem — brak dostępu do `t`); tłumaczenie dzieje się w miejscu użycia przez `t(MAP[x])`.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  FlaskConical,
  History,
  Pencil,
  Play,
  Plus,
  Trash2,
  Upload,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type ActionType = 'create_module_record' | 'create_invoice_document' | 'set_status';

interface RuleFilterConfig {
  matchMode?: 'all' | 'any';
  directions?: string[];
  counterpartyNipWhitelist?: string[];
  counterpartyNipBlacklist?: string[];
  counterpartyNameContains?: string;
  invoiceNumberContains?: string;
  currencies?: string[];
  minAmount?: number | null;
  maxAmount?: number | null;
  minNet?: number | null;
  maxNet?: number | null;
  invoiceTypes?: string[];
  issuedFrom?: string;
  issuedTo?: string;
}
interface ActionDto {
  type: ActionType;
  moduleId?: string;
  status?: string;
  startApproval?: boolean;
  fieldMapping?: Record<string, string>;
  categoryId?: string | null;
}
interface RuleActionConfig {
  actions?: ActionDto[];
  continueAfterMatch?: boolean;
  targetModuleId?: string;
  defaultStatus?: string;
}
interface RuleDto {
  id: string;
  companyId: string;
  name: string;
  priority: number;
  isActive: boolean;
  filterConfig: RuleFilterConfig;
  actionConfig: RuleActionConfig;
}
interface ModuleItem {
  id: string;
  name: string;
}
interface LogEntry {
  id: string;
  ruleName: string | null;
  ksefNumber: string | null;
  outcome: 'created' | 'failed' | 'skipped' | string;
  message: string | null;
  createdAt: string;
}
interface TestMatch {
  id: string;
  ksefNumber: string;
  invoiceNumber: string | null;
  counterpartyName: string | null;
  grossAmount: string | null;
  currency: string | null;
}

// Mapy trzymają KLUCZE i18n (poza komponentem — brak `t`); tłumaczenie w miejscu użycia.
const OUTCOME_META: Record<string, { labelKey: string; cls: string }> = {
  created: {
    labelKey: 'tenant.ksefAutomation.outcome.created',
    cls: 'bg-success-100 text-success-700 dark:bg-success-900/30',
  },
  failed: {
    labelKey: 'tenant.ksefAutomation.outcome.failed',
    cls: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30',
  },
  skipped: {
    labelKey: 'tenant.ksefAutomation.outcome.skipped',
    cls: 'bg-bg-tertiary text-text-secondary',
  },
};

const ACTION_LABELS: Record<ActionType, string> = {
  create_module_record: 'tenant.ksefAutomation.actionType.create_module_record',
  create_invoice_document: 'tenant.ksefAutomation.actionType.create_invoice_document',
  set_status: 'tenant.ksefAutomation.actionType.set_status',
};

// Eksportowane dla testu zgodności z listą statusów faktur w kolumnie „Status" (`lib/documents/invoice-select-labels`).
export const STATUS_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: 'new', labelKey: 'tenant.ksefAutomation.status.new' },
  { value: 'verified', labelKey: 'tenant.ksefAutomation.status.verified' },
  { value: 'booked', labelKey: 'tenant.ksefAutomation.status.booked' },
  { value: 'rejected', labelKey: 'tenant.ksefAutomation.status.rejected' },
];

interface ActionForm {
  type: ActionType;
  moduleId: string;
  status: string;
  startApproval: boolean;
  fieldMapping: Record<string, string>;
  categoryId: string;
}

/** Pola faktury KSeF dostępne do mapowania na pola modułu (labelKey — klucz i18n). */
const INVOICE_SOURCE_FIELDS: Array<{ key: string; labelKey: string }> = [
  { key: 'ksefNumber', labelKey: 'tenant.ksefAutomation.invoiceField.ksefNumber' },
  { key: 'invoiceNumber', labelKey: 'tenant.ksefAutomation.invoiceField.invoiceNumber' },
  { key: 'invoiceType', labelKey: 'tenant.ksefAutomation.invoiceField.invoiceType' },
  { key: 'sellerNip', labelKey: 'tenant.ksefAutomation.invoiceField.sellerNip' },
  { key: 'sellerName', labelKey: 'tenant.ksefAutomation.invoiceField.sellerName' },
  { key: 'buyerNip', labelKey: 'tenant.ksefAutomation.invoiceField.buyerNip' },
  { key: 'buyerName', labelKey: 'tenant.ksefAutomation.invoiceField.buyerName' },
  { key: 'netAmount', labelKey: 'tenant.ksefAutomation.invoiceField.netAmount' },
  { key: 'vatAmount', labelKey: 'tenant.ksefAutomation.invoiceField.vatAmount' },
  { key: 'grossAmount', labelKey: 'tenant.ksefAutomation.invoiceField.grossAmount' },
  { key: 'currency', labelKey: 'tenant.ksefAutomation.invoiceField.currency' },
  { key: 'issueDate', labelKey: 'tenant.ksefAutomation.invoiceField.issueDate' },
  { key: 'saleDate', labelKey: 'tenant.ksefAutomation.invoiceField.saleDate' },
  { key: 'dueDate', labelKey: 'tenant.ksefAutomation.invoiceField.dueDate' },
];
interface RuleForm {
  id?: string;
  companyId: string;
  name: string;
  priority: string;
  isActive: boolean;
  continueAfterMatch: boolean;
  matchMode: 'all' | 'any';
  direction: '' | 'purchase' | 'sales';
  nipWhitelist: string;
  nipBlacklist: string;
  nameContains: string;
  invoiceNumberContains: string;
  currencies: string;
  minAmount: string;
  maxAmount: string;
  minNet: string;
  maxNet: string;
  invoiceTypes: string;
  issuedFrom: string;
  issuedTo: string;
  actions: ActionForm[];
}

const KEY = ['ksef-automation-rules'] as const;
const inputCls =
  'w-full rounded-md border border-border-primary bg-bg-primary px-2.5 py-1.5 text-sm text-text-primary';
const labelCls = 'mb-1 block text-xs font-medium text-text-secondary';

function newAction(): ActionForm {
  return {
    type: 'create_invoice_document',
    moduleId: '',
    status: 'booked',
    startApproval: false,
    fieldMapping: {},
    categoryId: '',
  };
}

function emptyForm(companyId: string): RuleForm {
  return {
    companyId,
    name: '',
    priority: '0',
    isActive: true,
    continueAfterMatch: false,
    matchMode: 'all',
    direction: 'purchase',
    nipWhitelist: '',
    nipBlacklist: '',
    nameContains: '',
    invoiceNumberContains: '',
    currencies: '',
    minAmount: '',
    maxAmount: '',
    minNet: '',
    maxNet: '',
    invoiceTypes: '',
    issuedFrom: '',
    issuedTo: '',
    actions: [newAction()],
  };
}

const csv = (s: string): string[] =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

function ruleToForm(r: RuleDto): RuleForm {
  const f = r.filterConfig ?? {};
  const ac = r.actionConfig ?? {};
  const rawActions: ActionDto[] =
    ac.actions && ac.actions.length > 0
      ? ac.actions
      : ac.targetModuleId
        ? [{ type: 'create_module_record', moduleId: ac.targetModuleId, status: ac.defaultStatus }]
        : [newAction()];
  return {
    id: r.id,
    companyId: r.companyId,
    name: r.name,
    priority: String(r.priority),
    isActive: r.isActive,
    continueAfterMatch: ac.continueAfterMatch ?? false,
    matchMode: f.matchMode === 'any' ? 'any' : 'all',
    direction: (f.directions?.[0] as RuleForm['direction']) ?? '',
    nipWhitelist: (f.counterpartyNipWhitelist ?? []).join(', '),
    nipBlacklist: (f.counterpartyNipBlacklist ?? []).join(', '),
    nameContains: f.counterpartyNameContains ?? '',
    invoiceNumberContains: f.invoiceNumberContains ?? '',
    currencies: (f.currencies ?? []).join(', '),
    minAmount: f.minAmount != null ? String(f.minAmount) : '',
    maxAmount: f.maxAmount != null ? String(f.maxAmount) : '',
    minNet: f.minNet != null ? String(f.minNet) : '',
    maxNet: f.maxNet != null ? String(f.maxNet) : '',
    invoiceTypes: (f.invoiceTypes ?? []).join(', '),
    issuedFrom: f.issuedFrom ?? '',
    issuedTo: f.issuedTo ?? '',
    actions: rawActions.map((a) => ({
      type: a.type,
      moduleId: a.moduleId ?? '',
      status: a.status ?? 'booked',
      startApproval: a.startApproval ?? false,
      fieldMapping: a.fieldMapping ?? {},
      categoryId: a.categoryId ?? '',
    })),
  };
}

function buildFilterConfig(f: RuleForm): RuleFilterConfig {
  const cfg: RuleFilterConfig = {};
  if (f.matchMode === 'any') cfg.matchMode = 'any';
  if (f.direction) cfg.directions = [f.direction];
  if (csv(f.nipWhitelist).length) cfg.counterpartyNipWhitelist = csv(f.nipWhitelist);
  if (csv(f.nipBlacklist).length) cfg.counterpartyNipBlacklist = csv(f.nipBlacklist);
  if (f.nameContains.trim()) cfg.counterpartyNameContains = f.nameContains.trim();
  if (f.invoiceNumberContains.trim()) cfg.invoiceNumberContains = f.invoiceNumberContains.trim();
  if (csv(f.currencies).length) cfg.currencies = csv(f.currencies).map((c) => c.toUpperCase());
  if (f.minAmount.trim()) cfg.minAmount = Number(f.minAmount);
  if (f.maxAmount.trim()) cfg.maxAmount = Number(f.maxAmount);
  if (f.minNet.trim()) cfg.minNet = Number(f.minNet);
  if (f.maxNet.trim()) cfg.maxNet = Number(f.maxNet);
  if (csv(f.invoiceTypes).length)
    cfg.invoiceTypes = csv(f.invoiceTypes).map((t) => t.toUpperCase());
  if (f.issuedFrom.trim()) cfg.issuedFrom = f.issuedFrom;
  if (f.issuedTo.trim()) cfg.issuedTo = f.issuedTo;
  return cfg;
}

function buildActionConfig(f: RuleForm): RuleActionConfig {
  const actions: ActionDto[] = f.actions.map((a) => {
    if (a.type === 'create_module_record') {
      const mapping = Object.fromEntries(Object.entries(a.fieldMapping).filter(([, v]) => v));
      return {
        type: a.type,
        moduleId: a.moduleId,
        ...(a.status ? { status: a.status } : {}),
        ...(Object.keys(mapping).length ? { fieldMapping: mapping } : {}),
        ...(a.categoryId ? { categoryId: a.categoryId } : {}),
      };
    }
    if (a.type === 'create_invoice_document') {
      return {
        type: a.type,
        ...(a.status ? { status: a.status } : {}),
        ...(a.startApproval ? { startApproval: true } : {}),
      };
    }
    return { type: 'set_status', status: a.status || 'booked' };
  });
  return { actions, ...(f.continueAfterMatch ? { continueAfterMatch: true } : {}) };
}

function actionValid(a: ActionForm): boolean {
  if (a.type === 'create_module_record') return !!a.moduleId;
  if (a.type === 'set_status') return !!a.status;
  return true;
}

/** Liczba ustawionych filtrów „zaawansowanych" (zwijanych) — do licznika przy „Więcej filtrów". */
function advancedFilterCount(f: RuleForm): number {
  return [
    f.nipWhitelist,
    f.nipBlacklist,
    f.nameContains,
    f.invoiceNumberContains,
    f.currencies,
    f.minAmount,
    f.maxAmount,
    f.minNet,
    f.maxNet,
    f.issuedFrom,
    f.issuedTo,
  ].filter((v) => v.trim() !== '').length;
}

/** Konfiguracja akcji „utwórz rekord w module": kategoria + mapowanie pól (pobiera pola/kategorie). */
function ModuleRecordActionConfig({
  action,
  onPatch,
}: {
  action: ActionForm;
  onPatch: (patch: Partial<ActionForm>) => void;
}) {
  const { t } = useTranslation();
  const fieldsQuery = useQuery({
    queryKey: ['ksef-module-fields', action.moduleId],
    enabled: !!action.moduleId,
    queryFn: async () => {
      const res = await api.get<{ fields: Array<{ slug: string; name: string }> }>(
        `/modules/${action.moduleId}`,
      );
      return res.data.fields ?? [];
    },
  });
  const catsQuery = useQuery({
    queryKey: ['ksef-module-cats', action.moduleId],
    enabled: !!action.moduleId,
    queryFn: async () => {
      const res = await api.get<{ categories: Array<{ id: string; name: string }> }>(
        `/modules/${action.moduleId}/categories`,
      );
      return res.data.categories ?? [];
    },
  });
  const fields = fieldsQuery.data ?? [];
  const cats = catsQuery.data ?? [];
  if (!action.moduleId) return null;

  return (
    <div className="space-y-2 rounded-md border border-border-primary/60 p-2">
      {cats.length > 0 && (
        <label className="block">
          <span className={labelCls}>{t('tenant.ksefAutomation.recordCategory')}</span>
          <Dropdown
            value={action.categoryId}
            onChange={(val) => onPatch({ categoryId: val })}
            options={[
              { value: '', label: t('tenant.ksefAutomation.noCategory') },
              ...cats.map((c) => ({ value: c.id, label: c.name })),
            ]}
            aria-label={t('tenant.ksefAutomation.recordCategory')}
          />
        </label>
      )}
      {fields.length > 0 && (
        <div>
          <span className={labelCls}>{t('tenant.ksefAutomation.fieldMapping')}</span>
          <div className="max-h-48 space-y-1 overflow-auto">
            {fields.map((f) => (
              <div key={f.slug} className="flex items-center gap-2">
                <span className="w-1/2 truncate text-xs text-text-secondary" title={f.name}>
                  {f.name}
                </span>
                <Dropdown
                  value={action.fieldMapping[f.slug] ?? ''}
                  onChange={(val) =>
                    onPatch({ fieldMapping: { ...action.fieldMapping, [f.slug]: val } })
                  }
                  options={[
                    { value: '', label: t('tenant.ksefAutomation.autoNone') },
                    ...INVOICE_SOURCE_FIELDS.map((s) => ({ value: s.key, label: t(s.labelKey) })),
                  ]}
                  className="w-1/2 px-2 py-1 text-xs"
                  aria-label={f.name}
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-text-muted">
            {t('tenant.ksefAutomation.mappingHint')}
          </p>
        </div>
      )}
    </div>
  );
}

export function KsefAutomationRulesSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);

  const [form, setForm] = useState<RuleForm | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<RuleDto | null>(null);
  const [pendingRun, setPendingRun] = useState<RuleDto | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importCompanyId, setImportCompanyId] = useState('');
  const [testResult, setTestResult] = useState<{ scanned: number; matches: TestMatch[] } | null>(
    null,
  );

  const rulesQuery = useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await api.get<{ rules: RuleDto[] }>('/ksef/automation-rules');
      return res.data.rules ?? [];
    },
  });
  const rules = rulesQuery.data ?? [];

  const modulesQuery = useQuery({
    queryKey: ['custom-modules-for-ksef'],
    queryFn: async () => {
      const res = await api.get<{ modules: ModuleItem[] }>('/modules');
      return res.data.modules ?? [];
    },
  });
  const modules = modulesQuery.data ?? [];

  const logQuery = useQuery({
    queryKey: ['ksef-automation-log'],
    enabled: showHistory,
    queryFn: async () => {
      const res = await api.get<{ log: LogEntry[] }>('/ksef/automation-log');
      return res.data.log ?? [];
    },
  });
  const logEntries = logQuery.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const patchForm = (patch: Partial<RuleForm>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  const patchAction = (idx: number, patch: Partial<ActionForm>) =>
    setForm((prev) =>
      prev
        ? { ...prev, actions: prev.actions.map((a, i) => (i === idx ? { ...a, ...patch } : a)) }
        : prev,
    );

  const saveMutation = useMutation({
    mutationFn: async (f: RuleForm) => {
      const body = {
        name: f.name.trim(),
        priority: Number(f.priority) || 0,
        isActive: f.isActive,
        filterConfig: buildFilterConfig(f),
        actionConfig: buildActionConfig(f),
      };
      if (f.id) return api.put(`/ksef/automation-rules/${f.id}`, body);
      return api.post('/ksef/automation-rules', { companyId: f.companyId, ...body });
    },
    onSuccess: () => {
      toast.show({ title: t('tenant.ksefAutomation.toast.saved'), variant: 'success' });
      setForm(null);
      setTestResult(null);
      invalidate();
    },
    onError: () =>
      toast.show({ title: t('tenant.ksefAutomation.toast.saveError'), variant: 'error' }),
  });

  const testMutation = useMutation({
    mutationFn: async (f: RuleForm) => {
      const res = await api.post<{ scanned: number; matchedCount: number; matches: TestMatch[] }>(
        '/ksef/automation-rules/test',
        { filterConfig: buildFilterConfig(f), companyId: f.companyId },
      );
      return res.data;
    },
    onSuccess: (data) => setTestResult({ scanned: data.scanned, matches: data.matches }),
    onError: () =>
      toast.show({ title: t('tenant.ksefAutomation.toast.testError'), variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/ksef/automation-rules/${id}`),
    onSuccess: () => {
      toast.show({ title: t('tenant.ksefAutomation.toast.deleted'), variant: 'success' });
      setPendingDelete(null);
      invalidate();
    },
    onError: () =>
      toast.show({ title: t('tenant.ksefAutomation.toast.deleteError'), variant: 'error' }),
  });

  const runMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ scanned: number; matched: number }>(
        `/ksef/automation-rules/${id}/run`,
        {},
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.show({
        title: t('tenant.ksefAutomation.ruleTriggered', {
          matched: data.matched,
          scanned: data.scanned,
        }),
        variant: 'success',
      });
      setPendingRun(null);
      qc.invalidateQueries({ queryKey: ['ksef-invoices'] });
      qc.invalidateQueries({ queryKey: ['ksef-automation-log'] });
    },
    onError: () =>
      toast.show({ title: t('tenant.ksefAutomation.toast.runError'), variant: 'error' }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (r: RuleDto) =>
      api.post('/ksef/automation-rules', {
        companyId: r.companyId,
        name: t('tenant.ksefAutomation.copySuffix', { name: r.name }),
        priority: r.priority,
        isActive: false,
        filterConfig: r.filterConfig ?? {},
        actionConfig: r.actionConfig ?? {},
      }),
    onSuccess: () => {
      toast.show({ title: t('tenant.ksefAutomation.toast.duplicated'), variant: 'success' });
      invalidate();
    },
    onError: () =>
      toast.show({ title: t('tenant.ksefAutomation.toast.duplicateError'), variant: 'error' }),
  });

  const priorityMutation = useMutation({
    mutationFn: async (input: { id: string; priority: number }) =>
      api.put(`/ksef/automation-rules/${input.id}`, { priority: Math.max(0, input.priority) }),
    onSuccess: () => invalidate(),
    onError: () =>
      toast.show({ title: t('tenant.ksefAutomation.toast.priorityError'), variant: 'error' }),
  });

  const exportRules = () => {
    const data = rules.map((r) => ({
      name: r.name,
      priority: r.priority,
      isActive: r.isActive,
      filterConfig: r.filterConfig ?? {},
      actionConfig: r.actionConfig ?? {},
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ksef-reguly-automatyzacji.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(importText) as unknown;
      if (!Array.isArray(parsed))
        throw new Error(t('tenant.ksefAutomation.importModal.errorExpectedArray'));
      let count = 0;
      for (const raw of parsed) {
        const r = raw as Partial<RuleDto>;
        await api.post('/ksef/automation-rules', {
          companyId: importCompanyId,
          name: r.name ?? t('tenant.ksefAutomation.importDefaultName'),
          priority: r.priority ?? 0,
          isActive: false,
          filterConfig: r.filterConfig ?? {},
          actionConfig: r.actionConfig ?? {},
        });
        count++;
      }
      return count;
    },
    onSuccess: (count) => {
      toast.show({
        title: t('tenant.ksefAutomation.imported', { count }),
        variant: 'success',
      });
      setImportOpen(false);
      setImportText('');
      invalidate();
    },
    onError: (err) =>
      toast.show({
        title:
          err instanceof Error
            ? t('tenant.ksefAutomation.toast.importFailedWithMsg', { msg: err.message })
            : t('tenant.ksefAutomation.toast.importFailed'),
        variant: 'error',
      }),
  });

  const canSubmit =
    form && form.name.trim() && form.actions.length > 0 && form.actions.every(actionValid);

  return (
    <div className="space-y-3 border-t border-border-primary pt-5">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <Zap className="h-4 w-4 text-accent-600" />
          {t('tenant.ksefAutomation.title')}
        </h3>
        <p className="text-sm text-text-secondary">{t('tenant.ksefAutomation.description')}</p>
      </div>

      {companies.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border-primary p-3 text-sm text-text-muted">
          {t('tenant.ksefAutomation.noCompanies')}
        </div>
      ) : (
        <>
          {!form && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setForm(emptyForm(companies[0]!.id));
                  setShowAdvanced(false);
                  setTestResult(null);
                }}
              >
                {t('tenant.ksefAutomation.addRule')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Download className="h-4 w-4" />}
                disabled={rules.length === 0}
                onClick={exportRules}
              >
                {t('tenant.ksefAutomation.export')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Upload className="h-4 w-4" />}
                onClick={() => {
                  setImportCompanyId(companies[0]!.id);
                  setImportOpen(true);
                }}
              >
                {t('tenant.ksefAutomation.import')}
              </Button>
            </div>
          )}

          {form && (
            <div className="space-y-4 rounded-xl border border-border-primary bg-bg-elevated p-4">
              <h4 className="text-sm font-semibold text-text-primary">
                {form.id ? t('tenant.ksefAutomation.editRule') : t('tenant.ksefAutomation.newRule')}
              </h4>

              {/* Podstawowe */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className={labelCls}>{t('tenant.ksefAutomation.ruleName')}</label>
                  <Input
                    type="text"
                    placeholder={t('tenant.ksefAutomation.ruleNamePlaceholder')}
                    value={form.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                  />
                </div>
                {!form.id && (
                  <div>
                    <label className={labelCls}>{t('tenant.ksefAutomation.company')}</label>
                    <Dropdown
                      value={form.companyId}
                      onChange={(val) => patchForm({ companyId: val })}
                      options={companies.map((c) => ({ value: c.id, label: c.name }))}
                      aria-label={t('tenant.ksefAutomation.company')}
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>{t('tenant.ksefAutomation.priority')}</label>
                  <Input
                    type="number"
                    min={0}
                    value={form.priority}
                    onChange={(e) => patchForm({ priority: e.target.value })}
                  />
                </div>
              </div>

              {/* Warunki */}
              <div className="space-y-2 rounded-lg border border-border-primary p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {t('tenant.ksefAutomation.conditions')}
                  </p>
                  <label className="flex items-center gap-1.5 text-xs text-text-secondary">
                    {t('tenant.ksefAutomation.matchWhen')}
                    <Dropdown
                      value={form.matchMode}
                      onChange={(val) => patchForm({ matchMode: val as 'all' | 'any' })}
                      options={[
                        { value: 'all', label: t('tenant.ksefAutomation.matchAll') },
                        { value: 'any', label: t('tenant.ksefAutomation.matchAny') },
                      ]}
                      className="w-auto px-2 py-1"
                      aria-label={t('tenant.ksefAutomation.matchWhen')}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{t('tenant.ksefAutomation.direction')}</label>
                    <Dropdown
                      value={form.direction}
                      onChange={(val) => patchForm({ direction: val as RuleForm['direction'] })}
                      options={[
                        { value: '', label: t('tenant.ksefAutomation.directionAny') },
                        { value: 'purchase', label: t('tenant.ksefAutomation.directionPurchase') },
                        { value: 'sales', label: t('tenant.ksefAutomation.directionSales') },
                      ]}
                      aria-label={t('tenant.ksefAutomation.direction')}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>{t('tenant.ksefAutomation.invoiceTypes')}</label>
                    <Input
                      type="text"
                      placeholder="FA, KOR"
                      value={form.invoiceTypes}
                      onChange={(e) => patchForm({ invoiceTypes: e.target.value })}
                    />
                  </div>
                </div>

                {/* „Więcej filtrów" — rzadziej używane warunki zwinięte, żeby nie przytłaczać ściany pól. */}
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-medium text-accent-600 hover:underline"
                  data-testid="ksef-rule-advanced-toggle"
                >
                  {showAdvanced ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                  {showAdvanced
                    ? t('tenant.ksefAutomation.lessFilters')
                    : t('tenant.ksefAutomation.moreFilters')}
                  {!showAdvanced && advancedFilterCount(form) > 0
                    ? ` ${t('tenant.ksefAutomation.activeFiltersSuffix', {
                        count: advancedFilterCount(form),
                      })}`
                    : ''}
                </button>

                {showAdvanced && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>{t('tenant.ksefAutomation.nipWhitelist')}</label>
                      <Input
                        type="text"
                        placeholder="5260000000, 8790000000"
                        value={form.nipWhitelist}
                        onChange={(e) => patchForm({ nipWhitelist: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>{t('tenant.ksefAutomation.nipBlacklist')}</label>
                      <Input
                        type="text"
                        placeholder="1112223344"
                        value={form.nipBlacklist}
                        onChange={(e) => patchForm({ nipBlacklist: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>{t('tenant.ksefAutomation.nameContains')}</label>
                      <Input
                        type="text"
                        value={form.nameContains}
                        onChange={(e) => patchForm({ nameContains: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>
                        {t('tenant.ksefAutomation.invoiceNumberContains')}
                      </label>
                      <Input
                        type="text"
                        value={form.invoiceNumberContains}
                        onChange={(e) => patchForm({ invoiceNumberContains: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>{t('tenant.ksefAutomation.currencies')}</label>
                      <Input
                        type="text"
                        placeholder="PLN, EUR"
                        value={form.currencies}
                        onChange={(e) => patchForm({ currencies: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelCls}>{t('tenant.ksefAutomation.grossFrom')}</label>
                        <Input
                          type="number"
                          value={form.minAmount}
                          onChange={(e) => patchForm({ minAmount: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t('tenant.ksefAutomation.grossTo')}</label>
                        <Input
                          type="number"
                          value={form.maxAmount}
                          onChange={(e) => patchForm({ maxAmount: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelCls}>{t('tenant.ksefAutomation.netFrom')}</label>
                        <Input
                          type="number"
                          value={form.minNet}
                          onChange={(e) => patchForm({ minNet: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t('tenant.ksefAutomation.netTo')}</label>
                        <Input
                          type="number"
                          value={form.maxNet}
                          onChange={(e) => patchForm({ maxNet: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelCls}>{t('tenant.ksefAutomation.issuedFrom')}</label>
                        <DatePicker
                          value={form.issuedFrom}
                          onChange={(val) => patchForm({ issuedFrom: val })}
                          className="w-full"
                          aria-label={t('tenant.ksefAutomation.issuedFrom')}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t('tenant.ksefAutomation.issuedTo')}</label>
                        <DatePicker
                          value={form.issuedTo}
                          onChange={(val) => patchForm({ issuedTo: val })}
                          className="w-full"
                          aria-label={t('tenant.ksefAutomation.issuedTo')}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Test na sucho */}
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<FlaskConical className="h-4 w-4" />}
                    loading={testMutation.isPending}
                    onClick={() => form && testMutation.mutate(form)}
                  >
                    {t('tenant.ksefAutomation.testOnReceived')}
                  </Button>
                  {testResult && (
                    <span className="text-xs text-text-secondary">
                      {t('tenant.ksefAutomation.testMatchResult', {
                        matched: testResult.matches.length,
                        scanned: testResult.scanned,
                      })}
                    </span>
                  )}
                </div>
                {testResult && testResult.matches.length > 0 && (
                  <div className="max-h-40 space-y-1 overflow-auto rounded border border-border-primary p-2 text-xs">
                    {testResult.matches.map((m) => (
                      <div key={m.id} className="flex justify-between gap-2 text-text-secondary">
                        <span className="truncate">
                          {m.counterpartyName ?? '—'} · {m.invoiceNumber ?? m.ksefNumber}
                        </span>
                        <span className="shrink-0">
                          {m.grossAmount ?? '—'} {m.currency ?? ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Akcje */}
              <div className="space-y-2 rounded-lg border border-border-primary p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {t('tenant.ksefAutomation.actionsTitle')}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => patchForm({ actions: [...form.actions, newAction()] })}
                  >
                    {t('tenant.ksefAutomation.addAction')}
                  </Button>
                </div>

                {form.actions.map((a, idx) => (
                  <div key={idx} className="space-y-2 rounded-md bg-bg-primary p-2">
                    <div className="flex items-center gap-2">
                      <Dropdown
                        value={a.type}
                        onChange={(val) => patchAction(idx, { type: val as ActionType })}
                        options={(Object.keys(ACTION_LABELS) as ActionType[]).map((at) => ({
                          value: at,
                          label: t(ACTION_LABELS[at]),
                        }))}
                        aria-label={t('tenant.ksefAutomation.actionsTitle')}
                      />
                      {form.actions.length > 1 && (
                        <button
                          type="button"
                          title={t('tenant.ksefAutomation.removeAction')}
                          className="shrink-0 rounded p-1.5 text-danger-600 hover:bg-bg-tertiary"
                          onClick={() =>
                            patchForm({ actions: form.actions.filter((_, i) => i !== idx) })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {a.type === 'create_module_record' && (
                      <>
                        <Dropdown
                          value={a.moduleId}
                          onChange={(val) => patchAction(idx, { moduleId: val })}
                          options={[
                            { value: '', label: t('tenant.ksefAutomation.selectModule') },
                            ...modules.map((m) => ({ value: m.id, label: m.name })),
                          ]}
                          aria-label={t('tenant.ksefAutomation.selectModule')}
                        />
                        <ModuleRecordActionConfig
                          action={a}
                          onPatch={(patch) => patchAction(idx, patch)}
                        />
                      </>
                    )}

                    {a.type === 'set_status' ? (
                      <Dropdown
                        value={a.status}
                        onChange={(val) => patchAction(idx, { status: val })}
                        options={STATUS_OPTIONS.map((s) => ({
                          value: s.value,
                          label: t(s.labelKey),
                        }))}
                        aria-label={t('tenant.ksefAutomation.statusAfterCreate')}
                      />
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-1 text-xs text-text-secondary">
                          {t('tenant.ksefAutomation.statusAfterCreate')}
                          <Dropdown
                            value={a.status}
                            onChange={(val) => patchAction(idx, { status: val })}
                            options={STATUS_OPTIONS.map((s) => ({
                              value: s.value,
                              label: t(s.labelKey),
                            }))}
                            className="w-auto px-2 py-1"
                            aria-label={t('tenant.ksefAutomation.statusAfterCreate')}
                          />
                        </label>
                        {a.type === 'create_invoice_document' && (
                          <label className="flex items-center gap-2 text-xs text-text-primary">
                            <input
                              type="checkbox"
                              checked={a.startApproval}
                              onChange={(e) =>
                                patchAction(idx, { startApproval: e.target.checked })
                              }
                            />
                            {t('tenant.ksefAutomation.startApproval')}
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => patchForm({ isActive: e.target.checked })}
                  />
                  {t('tenant.ksefAutomation.ruleActive')}
                </label>
                <label className="flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={form.continueAfterMatch}
                    onChange={(e) => patchForm({ continueAfterMatch: e.target.checked })}
                  />
                  {t('tenant.ksefAutomation.continueAfterMatch')}
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-border-primary pt-3">
                <Button variant="secondary" size="sm" onClick={() => setForm(null)}>
                  {t('tenant.ksefAutomation.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canSubmit || saveMutation.isPending}
                  loading={saveMutation.isPending}
                  onClick={() => form && saveMutation.mutate(form)}
                >
                  {t('tenant.ksefAutomation.save')}
                </Button>
              </div>
            </div>
          )}

          {/* Lista reguł */}
          <div className="space-y-2">
            {rulesQuery.isLoading && (
              <p className="text-sm text-text-muted">{t('tenant.ksefAutomation.loading')}</p>
            )}
            {!rulesQuery.isLoading && rules.length === 0 && (
              <div className="rounded-lg border border-dashed border-border-primary p-3 text-center text-sm text-text-muted">
                {t('tenant.ksefAutomation.noRules')}
              </div>
            )}
            {rules.map((r) => {
              const companyLabel = companies.find((c) => c.id === r.companyId)?.name ?? '—';
              const dir = r.filterConfig?.directions?.[0];
              const actionCount =
                r.actionConfig?.actions?.length ?? (r.actionConfig?.targetModuleId ? 1 : 0);
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-3 rounded-lg border border-border-primary p-3"
                >
                  <Zap
                    className={`h-4 w-4 shrink-0 ${r.isActive ? 'text-accent-600' : 'text-text-muted'}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-text-primary">{r.name}</div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
                      <span>{companyLabel}</span>
                      <span>·</span>
                      <span>
                        {t(
                          dir === 'sales'
                            ? 'tenant.ksefAutomation.directionSales'
                            : dir === 'purchase'
                              ? 'tenant.ksefAutomation.directionPurchase'
                              : 'tenant.ksefAutomation.directionAny',
                        )}
                      </span>
                      <span>·</span>
                      <span>
                        {t(
                          actionCount === 1
                            ? 'tenant.ksefAutomation.actionCountOne'
                            : 'tenant.ksefAutomation.actionCountOther',
                          { count: actionCount },
                        )}
                      </span>
                      {!r.isActive && (
                        <span className="rounded bg-bg-tertiary px-1.5 py-0.5">
                          {t('tenant.ksefAutomation.inactive')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col">
                    <button
                      type="button"
                      title={t('tenant.ksefAutomation.moveUp')}
                      className="rounded p-0.5 text-text-muted hover:bg-bg-tertiary"
                      onClick={() =>
                        priorityMutation.mutate({ id: r.id, priority: r.priority - 1 })
                      }
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      title={t('tenant.ksefAutomation.moveDown')}
                      className="rounded p-0.5 text-text-muted hover:bg-bg-tertiary"
                      onClick={() =>
                        priorityMutation.mutate({ id: r.id, priority: r.priority + 1 })
                      }
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <button
                    type="button"
                    title={t('tenant.ksefAutomation.runOnExisting')}
                    className="shrink-0 rounded p-1.5 text-accent-600 hover:bg-bg-tertiary"
                    onClick={() => setPendingRun(r)}
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title={t('tenant.ksefAutomation.duplicate')}
                    className="shrink-0 rounded p-1.5 text-text-secondary hover:bg-bg-tertiary"
                    onClick={() => duplicateMutation.mutate(r)}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title={t('tenant.ksefAutomation.edit')}
                    className="shrink-0 rounded p-1.5 text-text-secondary hover:bg-bg-tertiary"
                    onClick={() => {
                      const rf = ruleToForm(r);
                      setForm(rf);
                      // Rozwiń „Więcej filtrów" od razu, gdy reguła ma ustawione filtry zaawansowane.
                      setShowAdvanced(advancedFilterCount(rf) > 0);
                      setTestResult(null);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title={t('tenant.ksefAutomation.delete')}
                    className="shrink-0 rounded p-1.5 text-danger-600 hover:bg-bg-tertiary"
                    onClick={() => setPendingDelete(r)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Historia automatyzacji KSeF */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
        >
          <History className="h-4 w-4" />
          {showHistory
            ? t('tenant.ksefAutomation.hideHistory')
            : t('tenant.ksefAutomation.showHistory')}
        </button>

        {showHistory && (
          <div className="mt-2 space-y-1.5">
            {logQuery.isLoading && (
              <p className="text-sm text-text-muted">{t('tenant.ksefAutomation.loading')}</p>
            )}
            {!logQuery.isLoading && logEntries.length === 0 && (
              <p className="rounded-lg border border-dashed border-border-primary p-3 text-center text-sm text-text-muted">
                {t('tenant.ksefAutomation.noEvents')}
              </p>
            )}
            {logEntries.map((e) => {
              const meta = OUTCOME_META[e.outcome] ?? OUTCOME_META.skipped!;
              return (
                <div
                  key={e.id}
                  className="flex items-start gap-2 rounded-lg border border-border-primary px-3 py-2 text-xs"
                >
                  <span className={`rounded px-1.5 py-0.5 font-medium ${meta.cls}`}>
                    {t(meta.labelKey)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-text-primary">
                      {e.ruleName ?? '—'}
                      {e.ksefNumber && (
                        <span className="ml-1 font-mono text-text-secondary">· {e.ksefNumber}</span>
                      )}
                    </div>
                    {e.message && <div className="text-text-muted">{e.message}</div>}
                  </div>
                  <span className="shrink-0 text-text-muted">
                    {new Date(e.createdAt).toLocaleString('pl-PL')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.ksefAutomation.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.ksefAutomation.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('tenant.ksefAutomation.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingRun !== null}
        title={t('tenant.ksefAutomation.confirmRun.title')}
        message={
          pendingRun ? t('tenant.ksefAutomation.confirmRun.message', { name: pendingRun.name }) : ''
        }
        confirmLabel={t('tenant.ksefAutomation.confirmRun.confirm')}
        variant="primary"
        onConfirm={() => {
          if (pendingRun) runMutation.mutate(pendingRun.id);
        }}
        onClose={() => setPendingRun(null)}
      />

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('tenant.ksefAutomation.importModal.title')}
        size="md"
        testId="ksef-rules-import-modal"
        // Import w toku — tło i Esc nie zamykają okna (X i „Anuluj" działają).
        closeOnBackdrop={!importMutation.isPending}
        closeOnEsc={!importMutation.isPending}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setImportOpen(false)}>
              {t('tenant.ksefAutomation.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!importText.trim() || !importCompanyId}
              loading={importMutation.isPending}
              onClick={() => importMutation.mutate()}
            >
              {t('tenant.ksefAutomation.import')}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-xs text-text-secondary">
          {t('tenant.ksefAutomation.importModal.descBefore')}{' '}
          <strong>{t('tenant.ksefAutomation.importModal.descBold')}</strong>{' '}
          {t('tenant.ksefAutomation.importModal.descAfter')}
        </p>
        <label className={labelCls}>{t('tenant.ksefAutomation.importModal.targetCompany')}</label>
        <Dropdown
          value={importCompanyId}
          onChange={setImportCompanyId}
          options={companies.map((c) => ({ value: c.id, label: c.name }))}
          className="mb-2"
          aria-label={t('tenant.ksefAutomation.importModal.targetCompany')}
        />
        <textarea
          rows={8}
          placeholder='[ { "name": "...", "filterConfig": {...}, "actionConfig": {...} } ]'
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          className={`${inputCls} font-mono text-xs`}
        />
      </Modal>
    </div>
  );
}

export default KsefAutomationRulesSection;
